/**
 * @file        src/pages/account/CorporateAccounts.jsx
 * @description Corporate accounts (`business_accounts`) — CRUD, plus an
 *              Employees panel to add/deactivate the users (users.business_id)
 *              who log in under an account. Replaces AgentAccounts.jsx: the B2B
 *              reseller/agent concept is retired — going forward every new
 *              account defaults to account_type='corporate'. Legacy accounts
 *              still tagged 'b2b' stay visible/editable (not bulk-converted —
 *              a data decision out of scope for a UI rename).
 * @author      Kinetika Labs
 * @created     2026-07-10
 * @changelog
 *   - 2026-09-16 Claude — Password fields carry autoComplete="new-password" +
 *                          data-lpignore / data-1p-ignore, so the browser and
 *                          password managers stop pre-filling the signed-in
 *                          admin's own password into them. Only Login.jsx keeps
 *                          "current-password".
 *   - 2026-07-10 Claude — Initial (B2B agent accounts), as AgentAccounts.jsx.
 *   - 2026-07-24 Claude — Require agency name, contact email, contact phone,
 *                          GSTIN and PAN when creating an agent.
 *   - 2026-07-27 Claude — Renamed from AgentAccounts.jsx to CorporateAccounts.jsx;
 *                          new accounts default to account_type='corporate';
 *                          added the Employees management panel (add/deactivate
 *                          users under an account — the missing piece of
 *                          "corporate user creation").
 *   - 2026-08-03 Claude — Brands are registry-driven (BrandSelect/useBrands): no
 *                          hardcoded pair, no default brand — the form starts
 *                          blank and brand is required (server requires it too).
 *   - 2026-08-04 Claude — Agent retirement (CR E8): dropped the Reference Code
 *                          (agent_code) and Commission (%) fields/columns, and
 *                          the "B2B / Agent (legacy)" type-filter + account-type
 *                          option. All legacy rows were remapped to 'corporate'
 *                          by migration 035, so this screen now only manages
 *                          Corporate accounts. Employee/wallet/GST-PAN management
 *                          is unaffected.
 *   - 2026-08-10 Claude — GSTIN is optional now (PAN stays mandatory) —
 *                          matches the server and the new corporate
 *                          registration request flow.
 *   - 2026-08-04 Claude — B2B post-pay credit line (CR: Credit Dues admin UI,
 *                          migration 037): the edit modal now has a "Credit
 *                          line" section (enable toggle, limit, status, due
 *                          days) — the previously DB-only way to grant a
 *                          company post-pay credit. Outstanding dues are
 *                          managed on the new Credit Dues screen.
 *   - 2026-09-12 Claude — Add/Edit modal readability + a fix for a genuinely
 *                          UNREACHABLE control. Payment Mode was rendered only
 *                          while Pooled Wallet was already On, so an admin
 *                          creating an account could never see it — and since
 *                          a fresh form starts wallet-off, the wallet_only
 *                          policy was effectively un-settable at create time.
 *                          Every dependent control (payment mode, credit limit
 *                          / status / due days) now stays VISIBLE and is
 *                          disabled instead of unmounted, so the form no longer
 *                          reflows as toggles flip and the whole policy is
 *                          readable at a glance. Added a live "payment route"
 *                          preview (wallet → credit line → gateway, or the
 *                          wallet_only refusal) that mirrors what
 *                          booking_backend/utils/checkoutWallet.js actually
 *                          does at checkout, per-field hints, and the wider
 *                          `.lk-modal.wide` shell. NOTE: the maxWidth:720 the
 *                          Employees panel carried was inert — .lk-overlay
 *                          .lk-modal sets width:min(680px,100%), which caps
 *                          below it; it now uses the same wide class.
 *                          Display/layout only — no field, payload or
 *                          validation rule changed.
 *   - 2026-09-12 Claude — Streamlined the Add flow (server enforces all three
 *                          in services/businessAccounts.service.js; this form
 *                          only mirrors it):
 *                          (a) a "GST registered?" question — answer Yes and
 *                              GSTIN becomes mandatory, answer No and the field
 *                              is disabled and cleared. There is no
 *                              gst_registered column: the answer IS whether
 *                              gst_number is stored, so the two can't drift;
 *                          (b) a "Primary login" section on create. Every
 *                              corporate account is now born with its first
 *                              user (role b2b_owner) — an account nobody could
 *                              log into was the normal outcome before, since
 *                              adding the first employee was a separate,
 *                              skippable trip through the Employees panel. The
 *                              login email IS the contact email above, so
 *                              there is one address to keep unique;
 *                          (c) the pooled wallet defaults ON for a new account
 *                              and its hint says plainly that the wallet
 *                              belongs to the COMPANY — employees never have
 *                              one of their own (the admin per-user wallet
 *                              screen no longer lists them at all).
 *                          Also: API errors are now read as {error:{message}}
 *                          as well as {error:'…'} — the new conflict errors
 *                          come back in the former shape, and putting that
 *                          object into React state threw instead of showing
 *                          the message.
 *   - 2026-09-12 Claude — Delete now says what it does. With (b) every account
 *                          has a user, which the server's old guard read as
 *                          "has users — refuse", so the button could only
 *                          error; the server now refuses on real history
 *                          (bookings, wallet movement) and otherwise removes
 *                          the account with its own logins.
 *   - 2026-09-15 Claude — apiErrorMessage() moved to api/client.js so the other
 *                          screens share it; behaviour unchanged here.
 *   - 2026-09-15 Claude — CLIENT DECISION: these switches grant FACILITIES, they
 *                          no longer force a payment route. The customer picks
 *                          which instrument pays at checkout, so:
 *                          (a) the "Payment Mode" control is GONE. It chose
 *                              between wallet_plus_gateway and wallet_only, and
 *                              wallet_only is no longer enforced by
 *                              booking_backend/utils/checkoutWallet.js — leaving
 *                              the control would promise a restriction the
 *                              booking engine does not apply. The form no longer
 *                              sends `payment_mode` either; the column stays in
 *                              the schema, unread, so the decision is reversible.
 *                              The list's "Wallet only" chip goes with it;
 *                          (b) the payment-route preview now reads as what the
 *                              employee is OFFERED (gateway always included,
 *                              wallet and overdraft ticked by default) rather
 *                              than a fixed waterfall with a refusal at the end;
 *                          (c) the credit line is labelled "Overdraft", which is
 *                              what the client calls it. Same columns
 *                              (credit_enabled / credit_limit / credit_status /
 *                              dues_due_days) — wording only.
 *   - 2026-09-16 Claude — "Save doesn't update anything" on Edit. The save WAS
 *                          being refused; the refusal was just invisible, and
 *                          some of the refusals were this form's own doing:
 *                          (a) the message rendered as `.field-error.full`,
 *                              which spans nothing — the grid's span rule is
 *                              `.field.full` — so inside the 3-column
 *                              .form-grid it was a sliver of 12px red text in a
 *                              one-third cell, below the fold on a laptop.
 *                              It is a full-width banner now (admin.css), it
 *                              scrolls itself into view, and every check also
 *                              reports AT its field;
 *                          (b) the form accepted values the API refuses, and a
 *                              zod failure rejects the WHOLE body — so one
 *                              over-long Company Name / Contact Phone /
 *                              Address discarded every other edit in the same
 *                              save. The server's column widths (MAX) are now
 *                              maxLength on those inputs and checked before
 *                              submitting, and the email check matches zod's
 *                              (2+ char TLD) instead of being looser than it;
 *                          (c) a legacy row with a NULL `status` sent null,
 *                              which the status ENUM rejects (null is not
 *                              undefined) — it now falls back to 'active';
 *                          (d) the API's `error.fields` map is read back onto
 *                              the offending inputs, so "Invalid body" names
 *                              the field it means;
 *                          (e) a successful edit closed in silence, which looks
 *                              identical to one that never saved — it toasts
 *                              now, as create already did.
 *   - 2026-09-17 Claude — Both temporary-password fields are now
 *                          <PasswordInput>. This value is dictated to the
 *                          owner afterwards, so it has to be readable back.
 *   - 2026-09-25 Claude — `keepPreviousData: true` is a React Query **v4** option
 *                          and this workspace is on v5 (5.101), where no such
 *                          option exists and it was silently ignored. So every
 *                          page / filter / search change dropped `data` to
 *                          undefined, the table was swapped for six skeleton rows
 *                          and the panel collapsed ~700px before growing back —
 *                          the "pages flicker / the page moves" report. v5 spells
 *                          it `placeholderData: keepPreviousData` (a FUNCTION
 *                          imported from the package, not a boolean).
 */
import { useEffect, useRef, useState } from 'react';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import PageHead from '../../components/PageHead';
import Icon from '../../components/Icon';
import Chip from '../../components/Chip';
import Select from '../../components/Select';
import BrandSelect from '../../components/BrandSelect';
import Pager from '../../components/Pager';
import TableSkeleton from '../../components/TableSkeleton';
import { get, post, patch, del, apiErrorMessage } from '../../api/client';
import { useBrands } from '../../api/brands';
import { toast } from '../../lib/toast';
import { confirm } from '../../lib/confirm';
import PasswordInput from '../../components/PasswordInput';

const STATUS = ['active', 'inactive', 'suspended'];
const STATUS_OPTIONS = STATUS.map((s) => ({ value: s, label: s }));
// Account status is the gate corporateSpendGate() re-checks at spend time — a
// JWT minted before a status change must not keep spending the pool — so the
// hints say what each value actually does rather than restating the word.
const STATUS_HINT = {
  active: 'The company and its employees can book normally.',
  inactive: 'Employees are blocked at checkout. Existing bookings and wallet balance are untouched.',
  suspended: 'Same block as inactive, flagged as a deliberate hold (non-payment, compliance).',
};
const tone = (s) => (s === 'active' ? 'ok' : s === 'suspended' ? 'rej' : 'pend');

/**
 * What this company's employees are OFFERED at checkout, mirroring
 * booking_backend/utils/checkoutWallet.js. Preview only — it renders the saved
 * facilities, it does not decide anything.
 *
 * The two switches below grant FACILITIES; they no longer force a route (client
 * decision 2026-09-15). The employee chooses which of the offered instruments
 * actually pays for a given booking — a company can hold a healthy balance and
 * still want one booking on the card. So the gateway is ALWAYS in the list:
 * `payment_mode='wallet_only'` is not enforced anywhere any more, which is why
 * this screen stopped offering it (the column stays in the schema, unread).
 *
 * As of 2026-10-06 the route this form can describe is simply wallet -> gateway.
 * Credit is no longer configured on this record at all: it is an admin grant
 * carrying an expiry, made against the company's POOLED WALLET in Wallet
 * Management, and the portion the company spends becomes a due there. So this
 * preview deliberately says nothing about credit — the company's headroom is
 * whatever that wallet currently holds, which this screen does not know and must
 * not guess at.
 */
function paymentRoute(form) {
  const walletOn = Number(form.wallet_enabled) === 1;

  const steps = [];
  if (walletOn) steps.push('Company wallet');
  steps.push('Gateway (card / UPI)');

  const note = walletOn
    ? 'The wallet is ticked by default and pays as much as it can, with the gateway charged the remainder — so a short balance never blocks a booking. The employee can untick it to put a booking on the card instead. Any credit this company has been granted sits in that wallet; what it spends of it becomes an outstanding due.'
    : 'Every booking is paid in full at the card/UPI gateway, exactly like a retail customer. With the pooled wallet off there is nothing to grant credit against.';
  return { steps, note };
}

const EMPTY = {
  name: '', contact_email: '', contact_phone: '',
  address: '', gst_registered: false, gst_number: '', pan_number: '', status: 'active',
  // A corporate account's wallet belongs to the COMPANY and is shared by every
  // employee, so a new account starts with the pool on — it is the point of a
  // corporate account. Switch it off for a company that pays at the gateway.
  brand: '', account_type: 'corporate', b2b_enabled: 1, wallet_enabled: 1,
  // mirrors the column's own DB default (15).
  // The account's first user, created with the account (create only). Its login
  // email is contact_email above — deliberately not a separate field.
  owner_first_name: '', owner_last_name: '', owner_mobile: '', owner_password: '',
};

const YES_NO = [{ value: '1', label: 'Yes' }, { value: '0', label: 'No' }];

/**
 * The server's own limits, mirrored so this form can never submit a value the
 * API will refuse. They are the `business_accounts` column widths, enforced by
 * the zod body in admin_backend/src/routes/businessAccounts.routes.js — and a
 * zod failure rejects the WHOLE body, so one over-long field silently discarded
 * every other edit in the same save.
 */
const MAX = { name: 100, contact_email: 100, contact_phone: 15, address: 1000 };
// zod's .email() demands a 2+ character TLD; the looser check this form used
// accepted addresses (a@b.c) the API then bounced as a nameless "Invalid body".
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
const GSTIN_RE = /^[0-9A-Z]{15}$/;
const PAN_RE = /^[A-Z]{5}[0-9]{4}[A-Z]$/;
const cls = (invalid) => (invalid ? 'invalid' : undefined);

export default function CorporateAccounts() {
  const qc = useQueryClient();
  const [page, setPage] = useState(1);
  const pageSize = 25;
  const [q, setQ] = useState('');
  const [applied, setApplied] = useState('');
  const [brand, setBrand] = useState('');
  const [modal, setModal] = useState(null); // null | { mode:'create'|'edit', row? }
  const [employeesFor, setEmployeesFor] = useState(null); // null | row
  const { labelOf } = useBrands();

  const params = {
    page, pageSize,
    ...(applied ? { q: applied } : {}),
    ...(brand ? { brand } : {}),
  };
  const { data, isLoading } = useQuery({
    queryKey: ['business-accounts', 'list', params],
    queryFn: () => get('/business-accounts', { params }),
    placeholderData: keepPreviousData,
  });

  const rows = data?.data || [];
  const total = data?.meta?.total || 0;

  const delMut = useMutation({
    mutationFn: (id) => del(`/business-accounts/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['business-accounts'] }),
    onError: (e) => toast.err('Could not delete', apiErrorMessage(e, 'Could not delete this account.')),
  });

  return (
    <>
      <PageHead
        title="Corporate Accounts"
        subtitle="Account Management · corporate travel accounts"
        actions={
          <button className="btn primary" onClick={() => setModal({ mode: 'create' })}>
            <Icon name="plus" /> Add Corporate Account
          </button>
        }
      />

      <article className="panel glass">
        <form className="search-row" onSubmit={(e) => { e.preventDefault(); setPage(1); setApplied(q); }}>
          <div className="field">
            <label>Search</label>
            <input placeholder="Name, email, GSTIN" value={q} onChange={(e) => setQ(e.target.value)} />
          </div>
          <div className="field">
            <label>Brand</label>
            <BrandSelect
              value={brand}
              onChange={(v) => { setBrand(v); setPage(1); }}
              withAll
            />
          </div>
          <button className="btn primary" type="submit"><Icon name="search" /> Search</button>
        </form>

        <div className="table-wrap">
          {isLoading ? <TableSkeleton /> : (
            <table className="table">
              <thead>
                <tr>
                  <th>#</th><th>Account</th><th>Brand</th><th>Contact</th>
                  <th>Wallet</th><th>Status</th><th>Action</th>
                </tr>
              </thead>
              <tbody>
                {rows.length === 0 ? (
                  <tr><td colSpan={7} className="empty">No corporate accounts yet.</td></tr>
                ) : rows.map((b, idx) => (
                  <tr key={b.id}>
                    <td className="mono">{(page - 1) * pageSize + idx + 1}</td>
                    <td><b>{b.name}</b><small className="muted db">{b.contact_email || ''}</small>{Number(b.b2b_enabled) === 0 ? <small className="muted db">disabled</small> : null}</td>
                    <td><Chip tone="ok">{labelOf(b.brand)}</Chip></td>
                    <td>{b.contact_phone || '—'}</td>
                    <td>{Number(b.wallet_enabled) === 1 ? <Chip tone="ok">On</Chip> : <span className="muted">Off</span>}</td>
                    <td><Chip tone={tone(b.status)}>{b.status}</Chip></td>
                    <td>
                      <div className="row-actions">
                        <button className="btn xs ghost" onClick={() => setEmployeesFor(b)}>
                          <Icon name="users" /> Employees
                        </button>
                        <button className="btn xs ghost" onClick={() => setModal({ mode: 'edit', row: b })}>
                          <Icon name="edit" /> Edit
                        </button>
                        <button
                          className="btn xs danger"
                          onClick={async () => {
                            const message = `Delete "${b.name}" and its corporate logins? Only possible while the account has no bookings and no wallet history — otherwise set its status to inactive.`;
                            if (await confirm({ message, confirmLabel: 'Delete', danger: true })) delMut.mutate(b.id);
                          }}
                        >
                          <Icon name="trash" /> Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <Pager page={page} pageSize={pageSize} total={total} onPage={setPage} />
      </article>

      {modal ? <CorporateAccountModal state={modal} onClose={() => setModal(null)} /> : null}
      {employeesFor ? <EmployeesPanel account={employeesFor} onClose={() => setEmployeesFor(null)} /> : null}
    </>
  );
}

function CorporateAccountModal({ state, onClose }) {
  const qc = useQueryClient();
  const editing = state.mode === 'edit';
  // gst_registered is not stored: a saved GSTIN IS the "yes" answer, so an
  // existing account's toggle is read back off the column it governs.
  const [form, setForm] = useState(editing
    ? { ...EMPTY, ...state.row, gst_registered: Boolean(state.row.gst_number) }
    : EMPTY);
  const [err, setErr] = useState('');
  // Per-field messages, so a refusal is shown AT the field that caused it —
  // both this form's own checks and the API's `error.fields` map.
  const [fieldErr, setFieldErr] = useState({});
  const errRef = useRef(null);

  // The modal is taller than the viewport on a laptop, so an error rendered
  // next to the Save button can be off-screen at the moment it appears.
  useEffect(() => { if (err) errRef.current?.scrollIntoView({ block: 'center', behavior: 'smooth' }); }, [err]);

  // Typing in a field clears its own error — otherwise the message stays under
  // a value the admin has already fixed.
  const set = (k) => (e) => {
    setForm({ ...form, [k]: e.target.value });
    if (fieldErr[k]) setFieldErr({ ...fieldErr, [k]: undefined });
  };

  // Dependent controls stay mounted and go disabled instead of unmounting:
  // an admin has to be able to SEE the whole policy before deciding which
  // toggles to flip.
  const gstOn = Boolean(form.gst_registered);
  const route = paymentRoute(form);

  const save = useMutation({
    mutationFn: () => {
      const body = {
        name: form.name.trim(),
        contact_email: form.contact_email?.trim() || '',
        contact_phone: form.contact_phone?.trim() || '',
        address: form.address?.trim() || '',
        // Not registered: send nothing rather than an empty string, so the
        // server clears any stale GSTIN instead of storing ''.
        gst_registered: gstOn,
        gst_number: gstOn ? form.gst_number?.trim().toUpperCase() || '' : '',
        pan_number: form.pan_number?.trim().toUpperCase() || '',
        // `status` is a nullable ENUM on the table, so a legacy row can carry
        // NULL — and the API's enum rejects null (it is not `undefined`), which
        // would 400 the whole save on a field the admin never touched.
        status: form.status || 'active',
        brand: form.brand,
        account_type: form.account_type || 'corporate',
        b2b_enabled: Number(form.b2b_enabled) ? 1 : 0,
        wallet_enabled: Number(form.wallet_enabled) ? 1 : 0,
        // payment_mode is deliberately NOT sent: 'wallet_only' is no longer
        // enforced at checkout, so this screen neither asks nor writes it.
      };
      if (editing) return patch(`/business-accounts/${state.row.id}`, body);
      // The first user is part of creating the account, not a follow-up step.
      return post('/business-accounts', {
        ...body,
        owner: {
          first_name: form.owner_first_name.trim(),
          last_name: form.owner_last_name.trim(),
          mobile: form.owner_mobile.trim(),
          password: form.owner_password,
        },
      });
    },
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey: ['business-accounts'] });
      if (!editing) {
        const login = res?.data?.owner?.email || form.contact_email.trim();
        toast.ok('Corporate account created', `${login} can sign in as the account owner.`);
      } else {
        // An edit used to close in silence, which looks the same as an edit
        // that never saved. Say what was written, from the SERVER's row.
        toast.ok('Corporate account updated', `${res?.data?.name || form.name.trim()} saved.`);
      }
      onClose();
    },
    onError: (e) => {
      // The API reports a schema failure as {error:{message:'Invalid body',
      // fields:{contact_phone:'…'}}} — the top-level message names no field, so
      // the fields map is what tells the admin which input to fix.
      const fields = e?.response?.data?.error?.fields;
      if (fields && typeof fields === 'object') {
        setFieldErr(Object.fromEntries(
          Object.entries(fields).map(([k, v]) => [k.split('.')[0], String(v)]),
        ));
      }
      setErr(apiErrorMessage(e, 'Save failed.'));
    },
  });

  /**
   * Every check the API would apply, run here so a refusal names its field
   * instead of coming back as one "Invalid body". Collects ALL the problems in
   * one pass — stopping at the first meant fixing one field only to be stopped
   * again by the next.
   */
  const onSubmit = (e) => {
    e.preventDefault(); setErr(''); setFieldErr({});
    const name = form.name?.trim() || '';
    const email = form.contact_email?.trim() || '';
    const phone = form.contact_phone?.trim() || '';
    const address = form.address?.trim() || '';
    const gst = form.gst_number?.trim().toUpperCase() || '';
    const pan = form.pan_number?.trim().toUpperCase() || '';
    const fe = {};

    if (!name) fe.name = 'Account name is required.';
    else if (name.length > MAX.name) fe.name = `Keep this to ${MAX.name} characters (this is ${name.length}).`;
    // Brand is a registry code now — there is no default storefront, so every
    // account (create or edit of a legacy blank) must pick one explicitly.
    if (!form.brand) fe.brand = 'Storefront brand is required.';
    // Identity fields + PAN are mandatory when creating an account (mirrors
    // the server). GSTIN is optional — not every company is GST-registered.
    // Edits of pre-existing accounts are not force-filled.
    if (!editing) {
      if (!email) fe.contact_email = 'Contact email is required.';
      if (!phone) fe.contact_phone = 'Contact phone is required.';
      if (!pan) fe.pan_number = 'PAN is required.';
      // The primary login is part of the account, so it is validated with it.
      if (!form.owner_first_name.trim()) fe.owner_first_name = "The primary user's first name is required.";
      if ((form.owner_password || '').length < 8) fe.owner_password = 'Temporary password must be at least 8 characters.';
    }
    // A GST-registered company must give its GSTIN; an unregistered one is
    // never asked for one (the field is disabled and the value dropped).
    if (gstOn && !gst) fe.gst_number = 'GSTIN is required for a GST-registered company.';
    // Format + length checks apply whenever a value is present (create or edit).
    // The length ones exist because these are the API's column widths: over them
    // the whole PATCH is refused, not just the offending field.
    if (email && !EMAIL_RE.test(email)) fe.contact_email = 'Enter a valid contact email.';
    else if (email.length > MAX.contact_email) fe.contact_email = `Keep this to ${MAX.contact_email} characters (this is ${email.length}).`;
    if (phone.length > MAX.contact_phone) fe.contact_phone = `Keep this to ${MAX.contact_phone} characters (this is ${phone.length}).`;
    if (address.length > MAX.address) fe.address = `Keep this to ${MAX.address} characters (this is ${address.length}).`;
    if (gstOn && gst && !GSTIN_RE.test(gst)) fe.gst_number = `GSTIN must be exactly 15 characters, A–Z and 0–9 (this is ${gst.length}).`;
    if (pan && !PAN_RE.test(pan)) fe.pan_number = 'PAN must look like ABCDE1234F.';

    const bad = Object.keys(fe);
    if (bad.length) {
      setFieldErr(fe);
      setErr(bad.length === 1 ? fe[bad[0]] : `${bad.length} fields need fixing before this can be saved — see the messages below each one.`);
      return;
    }
    save.mutate();
  };

  return (
    <div className="lk-overlay" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div className="lk-modal panel glass wide">
        <div className="lk-modal-head">
          <h3>{editing ? 'Edit Corporate Account' : 'Add Corporate Account'}</h3>
          <button className="ib" aria-label="Close" onClick={onClose}><Icon name="x" /></button>
        </div>
        <form className="form-grid" onSubmit={onSubmit}>
          <div className="field full"><label className="section-label">Company details</label></div>
          <div className="field">
            <label>Company Name <span className="req">*</span></label>
            <input
              value={form.name} onChange={set('name')} placeholder="e.g. Skyline Travels"
              maxLength={MAX.name} className={cls(fieldErr.name)}
            />
            {fieldErr.name ? <div className="field-error">{fieldErr.name}</div> : null}
          </div>
          <div className="field">
            <label>Contact Email <span className="req">*</span></label>
            <input
              type="email" value={form.contact_email} onChange={set('contact_email')}
              placeholder="accounts@company.com"
              maxLength={MAX.contact_email} className={cls(fieldErr.contact_email)}
            />
            {fieldErr.contact_email ? <div className="field-error">{fieldErr.contact_email}</div> : null}
            <small className="field-hint">
              {editing
                ? "Also the primary user's login. Changing it moves that login to the new address."
                : 'Also the login for the primary user below. Must not already belong to a customer, a corporate user, or an admin — the server checks all of them.'}
            </small>
          </div>
          <div className="field">
            <label>Contact Phone <span className="req">*</span></label>
            <input
              value={form.contact_phone} onChange={set('contact_phone')} placeholder="9876543210"
              maxLength={MAX.contact_phone} className={cls(fieldErr.contact_phone)}
            />
            {fieldErr.contact_phone ? <div className="field-error">{fieldErr.contact_phone}</div> : null}
          </div>
          <div className="field">
            <label>GST Registered <span className="req">*</span></label>
            <Select
              value={gstOn ? '1' : '0'}
              onChange={(v) => setForm({ ...form, gst_registered: v === '1', ...(v === '1' ? {} : { gst_number: '' }) })}
              options={YES_NO}
            />
            <small className="field-hint">
              {gstOn
                ? 'A GST-registered company must supply its GSTIN — it goes on every invoice raised for this account.'
                : 'No GSTIN is collected, and any previously saved one is cleared on save.'}
            </small>
          </div>
          <div className="field">
            <label>GSTIN {gstOn ? <span className="req">*</span> : null}</label>
            <input
              value={gstOn ? form.gst_number : ''}
              onChange={set('gst_number')}
              placeholder={gstOn ? '15 chars, e.g. 27ABCDE1234F1Z5' : 'Not applicable'}
              maxLength={15}
              disabled={!gstOn}
              className={cls(fieldErr.gst_number)}
            />
            {fieldErr.gst_number ? <div className="field-error">{fieldErr.gst_number}</div> : null}
          </div>
          <div className="field">
            <label>PAN <span className="req">*</span></label>
            <input
              value={form.pan_number} onChange={set('pan_number')} placeholder="ABCDE1234F"
              maxLength={10} className={cls(fieldErr.pan_number)}
            />
            {fieldErr.pan_number ? <div className="field-error">{fieldErr.pan_number}</div> : null}
          </div>
          <div className="field">
            <label>Status</label>
            <Select value={form.status} onChange={(v) => setForm({ ...form, status: v })} options={STATUS_OPTIONS} />
            <small className="field-hint">{STATUS_HINT[form.status]}</small>
          </div>

          <div className="field full"><label className="section-label">Corporate configuration</label></div>
          <div className="field">
            <label>Storefront (Brand) <span className="req">*</span></label>
            <BrandSelect
              value={form.brand}
              onChange={(v) => { setForm({ ...form, brand: v }); setFieldErr({ ...fieldErr, brand: undefined }); }}
              invalid={Boolean(fieldErr.brand)}
            />
            {fieldErr.brand ? <div className="field-error">{fieldErr.brand}</div> : null}
            <small className="field-hint">
              Which storefront this company books on. Decides its pricing, coupons and payment gateway. Immutable in practice — change it and the company's markups change with it.
            </small>
          </div>
          <div className="field">
            <label>Live for booking</label>
            <Select
              value={String(form.b2b_enabled)}
              onChange={(v) => setForm({ ...form, b2b_enabled: Number(v) })}
              options={[
                { value: '1', label: 'Yes — live' },
                { value: '0', label: 'No — onboarding only' },
              ]}
            />
            <small className="field-hint">
              An onboarding flag shown on the list. To actually stop this company booking, set <b>Status</b> to inactive or suspended — that is the check checkout enforces.
            </small>
          </div>
          <div className="field">
            <label>Pooled Wallet</label>
            <Select
              value={String(form.wallet_enabled)}
              onChange={(v) => setForm({ ...form, wallet_enabled: Number(v) })}
              options={[
                { value: '0', label: 'Off — every booking pays at the gateway' },
                { value: '1', label: 'On — shared company wallet' },
              ]}
            />
            <small className="field-hint">
              The wallet belongs to the COMPANY, never to an individual: on, every employee spends ONE shared prepaid balance, topped up under Wallet Management → Company Wallets. Off, each booking goes straight to the card/UPI gateway. Corporate employees never hold a personal wallet either way.
            </small>
          </div>
          {/* There is deliberately no "Payment Mode" control here any more. It
              chose between wallet_plus_gateway and wallet_only, and wallet_only
              is no longer enforced at checkout (client decision 2026-09-15 — the
              gateway is always available to the employee), so offering it would
              promise a restriction the booking engine does not apply. The column
              is left in the schema, unread. */}

          {/* THE OVERDRAFT BLOCK IS GONE (client decision 2026-10-06,
              WALLET_DUES_SPEC.md). Overdraft on/off, Overdraft Limit, Overdraft
              Status and Due Days all lived here and all moved to one place:
              Wallet Management. Credit is now an admin grant carrying an EXPIRY
              — the grant amount replaced the limit, its expiry date replaced Due
              Days, and the portion the company SPENDS becomes a due on the
              pooled wallet. Freezing the wallet replaced the suspend switch.
              The four columns stay in the schema unread so the decision is
              reversible; this form no longer sends them and the API no longer
              accepts them. */}

          <div className="field full">
            <div className="pay-route">
              <span className="pay-route-cap">Payment route at checkout</span>
              <div className="pay-route-steps">
                {route.steps.map((step, i) => (
                  <span key={step} className="pay-route-step">
                    {i > 0 ? <span className="pay-route-arrow" aria-hidden="true">→</span> : null}
                    <Chip tone={step === 'Gateway (card / UPI)' ? 'pend' : 'ok'}>{step}</Chip>
                  </span>
                ))}
              </div>
              <small className="field-hint">{route.note}</small>
            </div>
          </div>

          <div className="field full">
            <label>Address</label>
            <input
              value={form.address} onChange={set('address')} placeholder="Registered address"
              maxLength={MAX.address} className={cls(fieldErr.address)}
            />
            {fieldErr.address ? <div className="field-error">{fieldErr.address}</div> : null}
          </div>

          {editing ? null : (
            <>
              <div className="field full">
                <label className="section-label">Primary login (first user)</label>
                <small className="field-hint">
                  Created with the account as its <b>Owner</b>, signing in with the contact email above. Further employees are added later from the account's Employees panel.
                </small>
              </div>
              <div className="field">
                <label>First Name <span className="req">*</span></label>
                <input
                  value={form.owner_first_name} onChange={set('owner_first_name')} placeholder="e.g. Priya"
                  maxLength={100} className={cls(fieldErr.owner_first_name)}
                />
                {fieldErr.owner_first_name ? <div className="field-error">{fieldErr.owner_first_name}</div> : null}
              </div>
              <div className="field">
                <label>Last Name</label>
                <input value={form.owner_last_name} onChange={set('owner_last_name')} placeholder="e.g. Sharma" />
              </div>
              <div className="field">
                <label>Mobile</label>
                <input value={form.owner_mobile} onChange={set('owner_mobile')} placeholder="Defaults to the contact phone" />
              </div>
              <div className="field">
                <label>Temporary Password <span className="req">*</span></label>
                <PasswordInput
                  value={form.owner_password}
                  onChange={set('owner_password')}
                  placeholder="Min. 8 characters"
                  autoComplete="new-password"
                  maxLength={72}
                  className={cls(fieldErr.owner_password)}
                />
                {fieldErr.owner_password ? <div className="field-error">{fieldErr.owner_password}</div> : null}
                <small className="field-hint">Share it with the owner; they sign in with it and can change it afterwards.</small>
              </div>
            </>
          )}

          {err ? <div className="field-error full" ref={errRef} role="alert">{err}</div> : null}
          <div className="form-actions full">
            <button type="button" className="btn ghost" onClick={onClose}>Cancel</button>
            <button className="btn primary" type="submit" disabled={save.isPending}>
              {save.isPending ? 'Saving…' : 'Save'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

const EMPTY_EMPLOYEE = { first_name: '', last_name: '', email: '', mobile: '', password: '', role: 'b2b_user' };

/** Add / list / deactivate the users (b2b_owner / b2b_user) who log in under a corporate account. */
function EmployeesPanel({ account, onClose }) {
  const qc = useQueryClient();
  const [page, setPage] = useState(1);
  const pageSize = 25;
  const [form, setForm] = useState(null); // null | employee form state
  const [err, setErr] = useState('');

  const { data, isLoading } = useQuery({
    queryKey: ['business-account-employees', account.id, page],
    queryFn: () => get(`/business-accounts/${account.id}/users`, { params: { page, pageSize } }),
    placeholderData: keepPreviousData,
  });
  const rows = data?.data || [];
  const total = data?.meta?.total || 0;

  const create = useMutation({
    mutationFn: (body) => post(`/business-accounts/${account.id}/users`, body),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['business-account-employees', account.id] }); setForm(null); },
    onError: (e) => setErr(e?.response?.data?.error?.message || e?.response?.data?.error || 'Could not add employee.'),
  });

  const toggleActive = useMutation({
    mutationFn: ({ userId, is_active }) => patch(`/business-accounts/${account.id}/users/${userId}`, { is_active }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['business-account-employees', account.id] }),
    onError: (e) => toast.err('Could not update', e?.response?.data?.error?.message || 'Could not update this employee.'),
  });

  const onSubmit = (e) => {
    e.preventDefault(); setErr('');
    if (!form.first_name.trim()) { setErr('First name is required.'); return; }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) { setErr('Enter a valid email.'); return; }
    if (form.password.length < 8) { setErr('Password must be at least 8 characters.'); return; }
    create.mutate({
      first_name: form.first_name.trim(), last_name: form.last_name.trim() || undefined,
      email: form.email.trim(), mobile: form.mobile.trim() || undefined,
      password: form.password, role: form.role,
    });
  };

  return (
    <div className="lk-overlay" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div className="lk-modal panel glass wide">
        <div className="lk-modal-head">
          <h3>Employees · {account.name}</h3>
          <button className="ib" aria-label="Close" onClick={onClose}><Icon name="x" /></button>
        </div>

        <div className="det-body">
          {form ? (
            <form className="form-grid" onSubmit={onSubmit}>
              <div className="field">
                <label>First Name <span className="req">*</span></label>
                <input value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} />
              </div>
              <div className="field">
                <label>Last Name</label>
                <input value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} />
              </div>
              <div className="field">
                <label>Email <span className="req">*</span></label>
                <input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
              </div>
              <div className="field">
                <label>Mobile</label>
                <input value={form.mobile} onChange={(e) => setForm({ ...form, mobile: e.target.value })} />
              </div>
              <div className="field">
                <label>Temporary Password <span className="req">*</span></label>
                <PasswordInput value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder="Min. 8 characters" autoComplete="new-password" data-lpignore="true" data-1p-ignore />
              </div>
              <div className="field">
                <label>Role</label>
                <Select
                  value={form.role}
                  onChange={(v) => setForm({ ...form, role: v })}
                  options={[
                    { value: 'b2b_user', label: 'Employee' },
                    { value: 'b2b_owner', label: 'Owner (primary login)' },
                  ]}
                />
              </div>
              {err ? <div className="field-error full">{err}</div> : null}
              <div className="form-actions full">
                <button type="button" className="btn ghost" onClick={() => { setForm(null); setErr(''); }}>Cancel</button>
                <button className="btn primary" type="submit" disabled={create.isPending}>
                  {create.isPending ? 'Adding…' : 'Add employee'}
                </button>
              </div>
            </form>
          ) : (
            <div className="form-actions" style={{ justifyContent: 'flex-start', marginBottom: 12 }}>
              <button className="btn primary" onClick={() => setForm(EMPTY_EMPLOYEE)}>
                <Icon name="plus" /> Add employee
              </button>
            </div>
          )}

          <div className="table-wrap">
            {isLoading ? <TableSkeleton /> : (
              <table className="table">
                <thead>
                  <tr><th>Name</th><th>Email</th><th>Role</th><th>Status</th><th>Action</th></tr>
                </thead>
                <tbody>
                  {rows.length === 0 ? (
                    <tr><td colSpan={5} className="empty">No employees yet.</td></tr>
                  ) : rows.map((u) => (
                    <tr key={u.id}>
                      <td><b>{`${u.first_name} ${u.last_name || ''}`.trim()}</b></td>
                      <td>{u.email}</td>
                      <td>{u.role === 'b2b_owner' ? 'Owner' : 'Employee'}</td>
                      <td><Chip tone={Number(u.is_active) ? 'ok' : 'rej'}>{Number(u.is_active) ? 'Active' : 'Inactive'}</Chip></td>
                      <td>
                        <button
                          className="btn xs ghost"
                          disabled={toggleActive.isPending}
                          onClick={() => toggleActive.mutate({ userId: u.id, is_active: Number(u.is_active) ? 0 : 1 })}
                        >
                          {Number(u.is_active) ? 'Deactivate' : 'Activate'}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
          <Pager page={page} pageSize={pageSize} total={total} onPage={setPage} />
        </div>
      </div>
    </div>
  );
}
